import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';

import { GreenhouseHealthCheckBanner_GreenhouseFragment } from 'src/generated/mloop-graphql';

import AppWideBanner, { AppWideBannerAction } from '../AppWideBanner';

interface Fragments {
  greenhouse: GreenhouseHealthCheckBanner_GreenhouseFragment;
}

interface Props {
  retry: AppWideBannerAction;
}

const GreenhouseHealthCheckBanner: FCWithFragments<Fragments, Props> = ({ greenhouse, retry }) => {
  const { t } = useTranslation();
  // hide if its not installed or is healthy
  if (!greenhouse || !greenhouse?.isInstalled || greenhouse?.isHealthy) {
    return null;
  }

  const endpoints = [
    !greenhouse.canReadUsers && t('hiring-dashboard-nav:nav.banner.endpoint.listUsers'),
    !greenhouse.canReadUser && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveUser'),
    !greenhouse.canReadJobs && t('hiring-dashboard-nav:nav.banner.endpoint.listJobs'),
    !greenhouse.canReadJob && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveJob'),
    !greenhouse.canReadJobStages && t('hiring-dashboard-nav:nav.banner.endpoint.listJobStages'),
    !greenhouse.canReadJobStageForJob && t('hiring-dashboard-nav:nav.banner.endpoint.listJobStageForJob'),
    !greenhouse.canReadJobPostings && t('hiring-dashboard-nav:nav.banner.endpoint.listJobPostings'),
    !greenhouse.canReadUserRoles && t('hiring-dashboard-nav:nav.banner.endpoint.listUserRoles'),
    !greenhouse.canReadEmailTemplates && t('hiring-dashboard-nav:nav.banner.endpoint.listEmailTemplates'),
    !greenhouse.canReadInterviews && t('hiring-dashboard-nav:nav.banner.endpoint.listInterviews'),
    !greenhouse.canReadInterview && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveInterview'),
    !greenhouse.canReadCandidates && t('hiring-dashboard-nav:nav.banner.endpoint.listCandidates'),
    !greenhouse.canReadCandidate && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveCandidate'),
    !greenhouse.canReadActivityFeed && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveActivityFeed'),
    !greenhouse.canReadApplications && t('hiring-dashboard-nav:nav.banner.endpoint.listApplications'),
    !greenhouse.canReadApplication && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveApplication'),
    !greenhouse.canReadUserPermission && t('hiring-dashboard-nav:nav.banner.endpoint.listUserPermission'),
  ].filter(Boolean) as string[];

  const text =
    endpoints.length > 0
      ? t('hiring-dashboard-nav:nav.banner.integrationUnhealthyWithEndpoints', {
          integrationName: 'Greenhouse',
          endpoints,
        })
      : t('hiring-dashboard-nav:nav.banner.integrationUnhealthy', { integrationName: 'Greenhouse' });

  return <AppWideBanner color="foreground" backgroundColor="error" text={text} action={retry} />;
};

GreenhouseHealthCheckBanner.fragments = {
  greenhouse: gql`
    fragment GreenhouseHealthCheckBanner_greenhouse on GreenhouseIntegrationHealth {
      isHealthy
      isInstalled
      canReadUsers
      canReadUser
      canReadJobs
      canReadJob
      canReadJobStages
      canReadJobStageForJob
      canReadJobPostings
      canReadUserRoles
      canReadEmailTemplates
      canReadInterviews
      canReadInterview
      canReadCandidates
      canReadCandidate
      canReadActivityFeed
      canReadApplications
      canReadApplication
      canReadUserPermission
    }
  `,
};

export default GreenhouseHealthCheckBanner;
